import React, { useEffect, useMemo, useState } from 'react';
import { Form, Table, Button, Modal } from 'react-bootstrap';
import numeral from 'numeral';
import pluralize from 'pluralize';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { mkConfig, generateCsv, asBlob } from 'export-to-csv';
import {
  faCheck,
  faFileArrowDown,
  faGear,
  faMagnifyingGlass,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';

import dayjs from '../../../../lib/day';
import CmFilterSelect from './CmFilterSelect';
import { useGenerateReport } from '../../../../services/reports';
import { capitalizeFirstLetter } from '../../../../lib/strings';
import {
  AGING_REPORT_HEADERS,
  OPEN_REPORT_HEADERS,
  OUTSTANDING_INVOICES_HEADERS,
  TIMELINE_REPORT_HEADERS,
} from '../../../../lib/constants';
import CustomDropdown from '../../../common/CustomDropdown';

const StyledSearchContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;

  .search-icon {
    position: absolute;
    left: 12px;
    color: #6c757d;
    pointer-events: none;
  }

  .search-input {
    padding-left: 36px;

    &:focus {
      border-color: #80bdff;
      box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
    }
  }
`;

const DATE_FORMAT = 'MMMM D, YYYY h:mm A';

const CsvConfig = mkConfig({
  columnHeaders: Object.entries(AGING_REPORT_HEADERS).map(([key, value]) => ({
    key,
    displayLabel: value,
  })),
});

const REPORT_HEADERS = {
  aging: AGING_REPORT_HEADERS,
  outstanding: OUTSTANDING_INVOICES_HEADERS,
  open: OPEN_REPORT_HEADERS,
  timeline: TIMELINE_REPORT_HEADERS,
};

const Toolbar = ({
  data,
  total,
  totalMin = 0,
  totalMax = 0,
  form,
  onChange,
  onSubmit,
  columns,
  view,
  onViewChange,
  isAdmin = false,
  canViewAllClaims = false,
  // Upcoming calls + Activity pop-outs, beside the search (2026-10-09).
  quickPanels = null,
}) => {
  const [reportType, setReportType] = useState(undefined);
  const [reportViewed, setReportViewed] = useState(false);
  const [show, setShow] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const searchTimeoutRef = React.useRef(null);
  const searchInputRef = React.useRef(null);

  const { data: report } = useGenerateReport(reportType);

  // Use passed totalMin/totalMax if available, otherwise calculate from data
  const [minValue, maxValue] = useMemo(() => {
    if (totalMin > 0 || totalMax > 0) {
      return [totalMin, totalMax];
    }

    let min = 0;
    let max = 0;

    data.forEach((i) => {
      min += i._minValue || 0;
      max += i._maxValue || 0;
    });

    return [min, max];
  }, [data, totalMin, totalMax]);

  const viewCols = useMemo(() => {
    return columns.map((c) => {
      const isChecked = view.includes(c.id);

      return {
        label: (
          <>
            {isChecked ? (
              <small className="me-2">
                <FontAwesomeIcon icon={faCheck} fixedWidth />
              </small>
            ) : null}
            <span>{c.label}</span>
          </>
        ),
        onClick: () => {
          if (isChecked) {
            onViewChange(view.filter((v) => v !== c.id));
          } else {
            onViewChange([...view, c.id]);
          }
        },
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const handleDownloadReport = () => {
    const csv = generateCsv(CsvConfig)(report.results);
    const blob = asBlob(CsvConfig)(csv);
    const url = window.URL.createObjectURL(blob);
    const tempLink = document.createElement('a');
    tempLink.href = url;
    tempLink.setAttribute(
      'download',
      `${reportType}-report-${dayjs().format('YYYY-MM-DD')}.csv`,
    );
    tempLink.click();
  };

  const handleModalClose = () => {
    setShow(false);
    setReportViewed(true);
  };

  useEffect(() => {
    if (!report) return;
    if (show) return;
    if (reportViewed) return;
    setShow(true);
  }, [report, show, reportViewed]);

  // Auto-trigger search on input with debounce
  const handleSearchChange = (e) => {
    onChange(e);

    // Clear existing timeout (cancels previous pending search)
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      setIsSearching(false);
    }

    // Debounce search by 400ms - only trigger after user stops typing
    searchTimeoutRef.current = setTimeout(() => {
      setIsSearching(true);
      const submitEvent = new Event('submit', { bubbles: true });
      e.target.closest('form').dispatchEvent(submitEvent);
    }, 400);
  };

  // Clear loading state once data arrives
  useEffect(() => {
    setIsSearching(false);
  }, [data]);

  // Focus search input on "/" key press (power user shortcut)
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Check if user is not already typing in an input/textarea
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }

      // Focus on "/" key
      if (e.key === '/') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    // One row on desktop; on a phone, search full width, then the quick
    // panels, then the count with the filters (Nic 2026-10-09).
    <div className="mb-4 flex flex-col gap-2.5 border-b border-slate-200 pb-3 dark:border-white/10 lg:flex-row lg:items-center lg:justify-between">
      <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center">
        <form onSubmit={onSubmit} className="w-full sm:w-[260px]">
          <StyledSearchContainer>
            <FontAwesomeIcon
              icon={isSearching ? faSpinner : faMagnifyingGlass}
              className={`search-icon ${isSearching ? 'fa-spin' : ''}`}
              fixedWidth
            />
            <Form.Control
              ref={searchInputRef}
              name="search"
              value={form.search || ''}
              onChange={handleSearchChange}
              placeholder="Search..."
              className="search-input w-full"
            />
          </StyledSearchContainer>
        </form>
        {quickPanels ? <div className="flex flex-wrap items-center gap-2">{quickPanels}</div> : null}
      </div>
      <div className="flex items-center gap-2">
        <span className="whitespace-nowrap text-sm font-semibold tabular-nums text-slate-600 dark:text-slate-300">
          {isAdmin ? (
            <>
              {numeral(minValue).format('$0,0')} - {numeral(maxValue).format('$0,0')}
              <span className="mx-1.5 text-slate-300 dark:text-slate-600">&middot;</span>
            </>
          ) : null}
          {numeral(total).format('0,0')} {pluralize('claim', total)}
        </span>
        <div className="d-none">
          <Form.Group>
            <Form.Select
              value={reportType}
              onChange={(e) => setReportType(e.target.value)}
            >
              <option value="">View Report</option>
              <option value="aging">Aging Report</option>
              <option value="outstanding">Outstanding Invoices Report</option>
              <option value="open">Open Cases Report</option>
              <option value="timeline">Timeline Report</option>
            </Form.Select>
          </Form.Group>
        </div>
        {/* Filter by Claims Manager: only for people who see every claim
            (Nic 2026-10-09). A CM's board is already just their own, so
            anyone else would always come back empty. */}
        {canViewAllClaims ? (
          <div className="ml-auto min-w-0 flex-1 sm:w-[240px] sm:flex-none lg:ml-0">
            <CmFilterSelect
              value={form.user || null}
              onChange={(val) => onChange({ target: { name: 'user', value: val } })}
            />
          </div>
        ) : null}
        <div className={canViewAllClaims ? '' : 'ml-auto'}>
          <CustomDropdown
            label={<FontAwesomeIcon icon={faGear} fixedWidth />}
            variant="link"
            menuItems={viewCols}
            className="text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-100"
          />
        </div>
      </div>
      <Modal size="xl" show={show} onHide={handleModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>{`${capitalizeFirstLetter(
            reportType,
          )} Report - ${dayjs().format(DATE_FORMAT)}`}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                {reportType
                  ? Object.values(REPORT_HEADERS[reportType || 'aging']).map(
                      (header, index) => (
                        <th
                          className={`${index === 0 ? 'sr-only' : ''}`}
                          key={index}
                        >
                          {header}
                        </th>
                      ),
                    )
                  : null}
              </tr>
            </thead>
            <tbody>
              {report && report.results
                ? report.results.map((row, index) => (
                    <tr key={index}>
                      {Object.values(row).map((value, index) => (
                        <td
                          className={`${index === 0 ? 'sr-only' : ''}`}
                          key={index}
                        >
                          {value}
                        </td>
                      ))}
                    </tr>
                  ))
                : null}
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="danger" onClick={handleModalClose}>
            Close
          </Button>
          <Button variant="success" onClick={handleDownloadReport}>
            <FontAwesomeIcon className="mx-2" icon={faFileArrowDown} />
            Download Report
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Toolbar;
